// bre-ticketing-events: the service's own domain event stream.
//
// Envelope is identical to the POS -> loyalty contract so consumers across
// BRE share one idempotency rule: INSERT the event_id first, act only if it
// was new. Every message is written to the transactional outbox in the same
// D1 batch as the change it describes (docs/architecture.md#outbox).
import { z } from 'zod';
import { SalesChannel, ScanResult, TenderType } from './enums';
import { BreAuthSub, BusinessDate, Cents, Id, NonNegativeCents, Timestamp } from './primitives';

export function eventEnvelope<const T extends string, D extends z.ZodType>(eventType: T, version: number, data: D) {
  return z.object({
    event_id: Id,
    event_type: z.literal(eventType),
    version: z.literal(version),
    occurred_at: Timestamp,
    data,
  });
}

export const TicketOrderCompletedV1 = eventEnvelope(
  'ticket_order.completed',
  1,
  z.object({
    order_id: Id,
    order_number: z.string(),
    channel: SalesChannel,
    customer_sub: BreAuthSub.nullable(),
    business_date: BusinessDate,
    completed_at: Timestamp,
    event_ids: z.array(Id).min(1),
    performance_ids: z.array(Id).min(1),
    ticket_ids: z.array(Id),
    totals: z.object({
      face_value_cents: NonNegativeCents,
      discount_cents: NonNegativeCents,
      comp_cents: NonNegativeCents,
      fees_cents: NonNegativeCents,
      tax_cents: NonNegativeCents,
      customer_total_cents: NonNegativeCents,
    }),
    tenders: z.array(z.object({ tender_type: TenderType, amount_cents: NonNegativeCents })),
  }),
);

export const TicketOrderRefundedV1 = eventEnvelope(
  'ticket_order.refunded',
  1,
  z.object({
    order_id: Id,
    refund_id: Id,
    customer_sub: BreAuthSub.nullable(),
    business_date: BusinessDate,
    refunded_at: Timestamp,
    ticket_ids: z.array(Id),
    reversed_line_ids: z.array(Id).min(1),
    refund_total_cents: NonNegativeCents,
  }),
);

export const TicketIssuedV1 = eventEnvelope(
  'ticket.issued',
  1,
  z.object({ ticket_id: Id, order_id: Id, performance_id: Id, holder_sub: BreAuthSub.nullable(), credential_version: z.int().min(1) }),
);

export const TicketTransferredV1 = eventEnvelope(
  'ticket.transferred',
  1,
  z.object({
    ticket_id: Id,
    transfer_id: Id,
    from_sub: BreAuthSub.nullable(),
    to_sub: BreAuthSub,
    credential_version: z.int().min(2),
  }),
);

export const TicketReissuedV1 = eventEnvelope(
  'ticket.reissued',
  1,
  z.object({ ticket_id: Id, credential_version: z.int().min(2), reason: z.string() }),
);

export const TicketScannedV1 = eventEnvelope(
  'ticket.scanned',
  1,
  z.object({
    scan_id: Id,
    ticket_id: Id,
    performance_id: Id,
    gate_id: Id.nullable(),
    result: ScanResult,
    offline: z.boolean(),
  }),
);

export const TicketInvalidScanV1 = eventEnvelope(
  'ticket.invalid_scan',
  1,
  z.object({ scan_id: Id, ticket_id: Id.nullable(), performance_id: Id, result: ScanResult, reason_code: z.string().nullable() }),
);

export const EventSalesUpdatedV1 = eventEnvelope(
  'event.sales_updated',
  1,
  z.object({ event_id: Id, performance_id: Id, inventory_version: z.int().min(0) }),
);

export const SettlementUpdatedV1 = eventEnvelope(
  'settlement.updated',
  1,
  z.object({ settlement_id: Id, event_id: Id, organization_id: Id, status: z.string(), net_payable_cents: Cents.nullable() }),
);

export const TicketingEvent = z.discriminatedUnion('event_type', [
  TicketOrderCompletedV1,
  TicketOrderRefundedV1,
  TicketIssuedV1,
  TicketTransferredV1,
  TicketReissuedV1,
  TicketScannedV1,
  TicketInvalidScanV1,
  EventSalesUpdatedV1,
  SettlementUpdatedV1,
]);
export type TicketingEvent = z.infer<typeof TicketingEvent>;
